import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "wouter";
import { ROUTE_META, SITE_NAME, type RouteMeta } from "./routeMeta";

const OG_IMAGE_PATH = "opengraph.jpg";
const SITE_ORIGIN = "https://www.reframeyouth.com";
export const canonicalUrl = (path: string) => `${SITE_ORIGIN}${path === "/" ? "/" : path.replace(/\/$/, "")}`;

type PageMeta = Pick<RouteMeta, "title" | "description" | "path">;

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id: string, value: Record<string, unknown>) {
  let el = document.head.querySelector<HTMLScriptElement>(`script[data-json-ld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.jsonLd = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(value).replace(/</g, "\\u003c");
}

export function useJsonLd(id: string, value: Record<string, unknown> | null) {
  useEffect(() => {
    if (!value) return;
    // Product and article schemas replace the build-time page schema.
    // Organization/WebSite scripts do not use this page-owned identifier.
    const pageId = ["product", "blog-posting"].includes(id) ? "page" : id;
    setJsonLd(pageId, value);
    if (pageId === "page") {
      setMeta("property", "og:type", value["@type"] === "BlogPosting" ? "article" : "product");
      const image = Array.isArray(value.image) ? value.image[0] : value.image;
      if (typeof image === "string") {
        setMeta("property", "og:image", image);
        setMeta("name", "twitter:image", image);
      }
    }
    return () => {
      document.head.querySelector(`script[data-json-ld="${pageId}"]`)?.remove();
    };
  }, [id, value]);
}

/**
 * Sets the document title, meta description, canonical URL, and Open Graph /
 * Twitter tags for the current page. Call once at the top of each public page.
 */
function applyPageMeta({ title, description, path }: PageMeta) {
    const fullTitle = `${title} | ${SITE_NAME}`;
    const url = canonicalUrl(path);
    const image = `${SITE_ORIGIN}/${OG_IMAGE_PATH}`;

    document.title = fullTitle;
    setMeta("name", "description", description);
    setCanonical(url);

    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:locale", "en_US");
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image);

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);
}

export function usePageMeta(meta: PageMeta | null) {
  useEffect(() => {
    if (meta) applyPageMeta(meta);
  }, [meta?.title, meta?.description, meta?.path]);
}

/** Runs before page effects so dynamic Product/BlogPosting data can replace it. */
export function PublicRouteMetadata() {
  const [location] = useLocation();
  useLayoutEffect(() => {
    const meta = ROUTE_META[location]
      ?? (location === "/assessment" ? { ...ROUTE_META["/self-assessment"], path: location } : null)
      ?? (location === "/suggestions" ? {
        title: "Youth Ministry Suggestions",
        description: "Share ideas for Reframe Youth ministry tools and curriculum.",
        heading: "Youth Ministry Suggestions", path: location,
      } : null);
    document.querySelector('script[data-json-ld="page"]')?.remove();
    if (meta) {
      applyPageMeta(meta);
      setJsonLd("page", {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: meta.heading,
        description: meta.description,
        url: canonicalUrl(meta.path),
        publisher: { "@id": `${SITE_ORIGIN}/#organization` },
      });
    }
    return () => {
      document.querySelector('script[data-json-ld="page"]')?.remove();
    };
  }, [location]);
  return null;
}
